import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DocumentAttachment, BudgetSummary } from '@/types/domain';
import { CheckCircle2, ShieldCheck, FileText, ArrowRight, X } from 'lucide-react';

interface RequestReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmSubmit: () => void;
  isSubmitting: boolean;
  title: string;
  category: string;
  procurementType: 'Material' | 'Service';
  expenseType: string;
  priority: string;
  requiredDate: string;
  companyCode: string;
  plantCode: string;
  departmentCode: string;
  costCenterCode: string;
  justification: string;
  totalAmount: number;
  itemsCount: number;
  budgetSummary?: BudgetSummary | null;
  attachments?: DocumentAttachment[];
}

export const RequestReviewModal: React.FC<RequestReviewModalProps> = ({
  isOpen,
  onClose,
  onConfirmSubmit,
  isSubmitting,
  title,
  category,
  procurementType,
  expenseType,
  priority,
  requiredDate,
  companyCode: _companyCode,
  plantCode,
  departmentCode,
  costCenterCode,
  justification,
  totalAmount,
  itemsCount,
  budgetSummary,
  attachments = [],
}) => {
  const isOverBudget = budgetSummary ? totalAmount > budgetSummary.availableBudget : false;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-poppins">
          {/* Blurred Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-slate-950/70 backdrop-blur-md"
            onClick={() => !isSubmitting && onClose()}
          />

          {/* Modal Dialog Card */}
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, scale: 0.9, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 16 }}
            transition={{ type: 'spring', damping: 26, stiffness: 360 }}
            className="relative z-10 bg-surface dark:bg-[#121526] text-foreground border border-border/80 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden"
          >
            <div className="p-5 border-b border-border bg-muted/20 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-foreground">Review & Confirm Submission</h3>
                  <p className="text-[11px] font-semibold text-foreground-secondary">
                    Verify requisition summary prior to enterprise workflow routing
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={onClose}
                className="p-1.5 rounded-xl text-foreground-secondary hover:text-foreground hover:bg-muted/60 transition-colors cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto text-xs">
              {/* Header Summary Box */}
              <div className="p-4 rounded-2xl border border-border bg-muted/30 space-y-2.5">
                <div className="flex items-start justify-between gap-3">
                  <h4 className="font-black text-foreground text-sm leading-snug">{title}</h4>
                  <Badge variant="outline" className="font-mono text-[10px] font-bold shrink-0">
                    {procurementType}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-foreground-secondary pt-1 border-t border-border/50">
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-foreground-muted">Category</span>
                    <span className="font-bold text-foreground">{category}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-foreground-muted">Accounting</span>
                    <span className="font-bold text-foreground font-mono">{expenseType}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-foreground-muted">Priority</span>
                    <span className="font-bold text-foreground">{priority}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] uppercase font-bold text-foreground-muted">Required Date</span>
                    <span className="font-bold text-foreground font-mono">{requiredDate}</span>
                  </div>
                </div>
              </div>

              {/* Financial & Budget Summary */}
              <div className="p-4 rounded-2xl border border-primary/25 bg-primary/5 flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold text-foreground-muted uppercase tracking-wider block">
                    Total Estimated Value
                  </span>
                  <span className="text-xl font-black font-mono text-foreground">
                    ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="text-foreground-secondary text-[11px] font-semibold block mt-0.5">
                    ({itemsCount} {itemsCount === 1 ? 'line item' : 'line items'} configured)
                  </span>
                </div>
                {budgetSummary && (
                  <div className="text-right font-mono text-xs">
                    <span className="text-[10px] font-bold text-foreground-muted uppercase tracking-wider block">
                      Cost Center Budget
                    </span>
                    <span
                      className={`font-black text-sm ${
                        isOverBudget ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      ₹{budgetSummary.availableBudget.toLocaleString('en-IN')}
                    </span>
                    {isOverBudget && (
                      <Badge variant="destructive" className="mt-1 text-[10px] py-0 font-bold block">
                        Budget Exception Required
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Allocation Info Pill */}
              <div className="flex items-center justify-between p-3 rounded-xl border border-border/60 bg-muted/40 text-[11px] font-mono">
                <span className="text-foreground-muted">Allocation:</span>
                <span className="font-bold text-foreground">
                  Plant: {plantCode} • Dept: {departmentCode} • Cost Center: {costCenterCode}
                </span>
              </div>

              {/* Expected Workflow Preview */}
              <div className="p-3.5 rounded-2xl border border-border bg-muted/20 space-y-2">
                <span className="font-bold text-foreground block text-xs">Automated Workflow Routing</span>
                <div className="flex items-center gap-2 text-[11px] font-medium text-foreground-secondary">
                  <span className="py-1 px-2.5 rounded-lg bg-surface border border-border text-foreground font-bold shadow-2xs">
                    1. Requisition Submitted
                  </span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span className="py-1 px-2.5 rounded-lg bg-surface border border-border text-foreground font-bold shadow-2xs">
                    2. Department Approval
                  </span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span className="py-1 px-2.5 rounded-lg bg-surface border border-border text-foreground font-bold shadow-2xs">
                    3. SAP Sync
                  </span>
                </div>
              </div>

              {/* Business Justification Preview */}
              {justification && (
                <div className="space-y-1">
                  <span className="font-bold text-foreground block text-xs">Business Rationale</span>
                  <p className="p-3.5 rounded-2xl border border-border bg-muted/30 text-foreground-secondary text-xs leading-relaxed italic">
                    "{justification}"
                  </p>
                </div>
              )}

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="flex items-center gap-2 text-foreground-secondary text-xs">
                  <FileText className="h-3.5 w-3.5 text-primary" />
                  <span>{attachments.length} supporting attachment(s) attached.</span>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border bg-muted/20 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-xl font-bold cursor-pointer"
              >
                Back to Edit
              </Button>
              <Button
                size="sm"
                isLoading={isSubmitting}
                onClick={onConfirmSubmit}
                className="rounded-xl font-bold cursor-pointer"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Confirm & Submit Requisition
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
